import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Family } from '../types';

interface AuthContextType {
  currentUser: User | null;
  currentFamily: Family | null;
  login: (user: User, family?: Family) => void;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  validateUserData: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentFamily, setCurrentFamily] = useState<Family | null>(null);

  useEffect(() => {
    // Check if user and family are stored in localStorage
    const storedUser = localStorage.getItem('user');
    const storedFamily = localStorage.getItem('family');

    if (storedUser) {
      try {
        setCurrentUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('user');
      }
    }

    if (storedFamily) {
      try {
        setCurrentFamily(JSON.parse(storedFamily));
      } catch (error) {
        console.error('Error parsing stored family:', error);
        localStorage.removeItem('family');
      }
    }
  }, []);

  const login = (user: User, family?: Family) => {
    setCurrentUser(user);
    localStorage.setItem('user', JSON.stringify(user));

    if (family) {
      setCurrentFamily(family);
      localStorage.setItem('family', JSON.stringify(family));
    }
  };

  const logout = () => {
    setCurrentUser(null);
    setCurrentFamily(null);
    localStorage.removeItem('user');
    localStorage.removeItem('family');
    localStorage.removeItem('token');
  };

  // Function to check if user and family data are valid
  const validateUserData = () => {
    const storedUser = localStorage.getItem('user');
    const storedFamily = localStorage.getItem('family');
    const storedToken = localStorage.getItem('token');

    if (!storedUser || !storedFamily || !storedToken) {
      console.log('Missing user data, logging out...');
      logout();
      return false;
    }

    try {
      const user = JSON.parse(storedUser);
      const family = JSON.parse(storedFamily);

      // Check if user and family have required fields
      if (!user.id || !user.name || !user.email || !family.id || !family.name) {
        console.log('Invalid user data structure, logging out...');
        logout();
        return false;
      }

      return true;
    } catch (error) {
      console.log('Error parsing user data, logging out...');
      logout();
      return false;
    }
  };

  const isAuthenticated = currentUser !== null;
  const isAdmin = currentUser?.role === 'Admin';

  const value: AuthContextType = {
    currentUser,
    currentFamily,
    login,
    logout,
    isAuthenticated,
    isAdmin,
    validateUserData,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
